import React from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Divider } from 'primereact/divider';
import { Badge } from 'primereact/badge';

const BillPreview = ({ booking, billCalculation, onPrint, onClose }) => {
  const footer = (
    <div>
      <Button 
        label="Close" 
        icon="pi pi-times" 
        onClick={onClose} 
        className="p-button-text" 
      />
      <Button 
        label="Print Invoice" 
        icon="pi pi-print" 
        onClick={onPrint} 
        autoFocus 
      />
    </div>
  );

  const amountTemplate = (rowData, field) => {
    return <span className="text-right">${rowData[field]?.toFixed(2)}</span>;
  };

  const weightTemplate = (rowData) => {
    return <span>{rowData.unitWeight?.toFixed(2)} kg</span>;
  };

  const itemTypeTemplate = (rowData) => {
    return (
      <div>
        <strong>{rowData.itemType}</strong>
        {rowData.itemCategory && (
          <div className="text-sm text-gray-600">{rowData.itemCategory}</div>
        )}
        {rowData.treatment && (
          <div className="text-xs text-gray-500">Treatment: {rowData.treatment}</div>
        )}
      </div>
    );
  };

  const header = (
    <div className="text-center">
      <h2 className="mb-2">Service Invoice</h2>
      <Badge value={booking.bookingReference} severity="info" />
      <div className="text-sm text-gray-600 mt-2">
        Date: {new Date(booking.createdAt).toLocaleString()}
      </div>
    </div>
  );

  return (
    <Dialog
      header={header}
      visible={true}
      style={{ width: '800px' }}
      footer={footer}
      onHide={onClose}
      modal
      className="bill-preview-dialog"
    >
      <div id="bill-content">
        {/* Customer & Service Details */}
        <div className="grid mb-4">
          <div className="col-6">
            <h4>Pickup Details</h4>
            <div className="field">
              <label className="font-semibold">Address:</label>
              <div>{booking.pickupAddress}</div>
            </div>
            <div className="field">
              <label className="font-semibold">Date:</label>
              <div>{new Date(booking.pickupDate).toLocaleString()}</div>
            </div>
          </div>
          <div className="col-6">
            <h4>Delivery Details</h4>
            <div className="field">
              <label className="font-semibold">Address:</label>
              <div>{booking.deliveryAddress}</div>
            </div>
            <div className="field">
              <label className="font-semibold">Date:</label>
              <div>{new Date(booking.deliveryDate).toLocaleString()}</div>
            </div>
          </div>
        </div>

        <Divider />

        {/* Items Table */}
        <h4>Order Items</h4>
        <DataTable value={booking.orderItems} responsiveLayout="scroll" className="p-datatable-sm mb-4">
          <Column field="itemType" header="Item" body={itemTypeTemplate} />
          <Column field="quantity" header="Qty" style={{ width: '80px' }} />
          <Column 
            field="unitPrice" 
            header="Unit Price" 
            body={(rowData) => amountTemplate(rowData, 'unitPrice')}
            style={{ width: '100px' }} 
          />
          <Column 
            field="unitWeight" 
            header="Weight" 
            body={weightTemplate}
            style={{ width: '100px' }} 
          />
          <Column 
            field="totalPrice" 
            header="Total" 
            body={(rowData) => amountTemplate(rowData, 'totalPrice')}
            style={{ width: '100px' }} 
          />
        </DataTable>

        <Divider />

        {/* Summary */}
        <div className="grid justify-content-end">
          <div className="col-8">
            <div className="field mb-2">
              <span>Subtotal:</span>
            </div>
            <div className="field mb-2">
              <span>Weight Charge:</span>
            </div>
            <div className="field mb-2">
              <span>Tax (18%):</span>
            </div>
            <Divider />
            <div className="field font-bold">
              <span>Grand Total:</span>
            </div>
            <div className="field mt-2">
              <Badge value={booking.paymentStatus} severity={booking.paymentStatus === 'Paid' ? 'success' : 'warning'} />
            </div>
          </div>
          <div className="col-4 text-right">
            <div className="field mb-2">
              <span>${billCalculation.subtotal?.toFixed(2)}</span>
            </div>
            <div className="field mb-2">
              <span>${billCalculation.weightCharge?.toFixed(2)}</span>
            </div>
            <div className="field mb-2">
              <span>${billCalculation.taxAmount?.toFixed(2)}</span>
            </div>
            <Divider />
            <div className="field font-bold">
              <span className="text-primary text-xl">
                ${billCalculation.grandTotal?.toFixed(2)}
              </span>
            </div>
          </div>
        </div>

        {booking.specialInstructions && (
          <>
            <Divider />
            <div className="mt-3">
              <h4>Special Instructions:</h4>
              <p className="text-gray-600">{booking.specialInstructions}</p>
            </div>
          </>
        )}
      </div>

      <style>{`
        @media print {
          .bill-preview-dialog .p-dialog-header,
          .bill-preview-dialog .p-dialog-footer {
            display: none;
          }
          .bill-preview-dialog .p-dialog-content {
            padding: 0;
          }
        }
      `}</style>
    </Dialog>
  );
};

export default BillPreview;